import { useState, useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/data/atoms/utilsAtoms';
import Pagination from '@/components/Pagination/index';
import CloseIcon from '@mui/icons-material/Close';
import Toast from '@/utils/toastManager';
import { PORTAL_STATUS } from '@/Pages/admin/ChargebeePortal/constants/portalUiConstants';

const WebhookInbox = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', eventType: '' });
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const userData = useAtomValue(userInfoAtom);

  useEffect(() => {
    fetchWebhooks();
  }, [filters, page, itemsPerPage]);

  const fetchWebhooks = async () => {
    setLoading(true);
    const params = new URLSearchParams({ ...filters, page, limit: itemsPerPage });
    const response = await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/webhooks?${params}`,
      {
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    const data = await response.json();
    setWebhooks(data.data.webhooks);
    setTotal(data.data.total);
    setLoading(false);
  };

  const handleReprocess = async (eventId) => {
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/webhooks/${eventId}/reprocess`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    Toast.success('Webhook reprocessed successfully');
    fetchWebhooks();
  };

  const handleForceLink = async (eventId) => {
    await fetch(
      `${import.meta.env.VITE_APP_API_URL}/portal/webhooks/${eventId}/force-link`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${userData.token}` }
      }
    );
    Toast.success('Webhook force linked successfully');
    fetchWebhooks();
  };

  return (
    <div>
      <div className="webhook-filters">
        <select
          className="filter-select"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>

        <select
          className="filter-select"
          value={filters.eventType}
          onChange={(e) => setFilters({ ...filters, eventType: e.target.value })}
        >
          <option value="">All Event Types</option>
          <option value="customer_created">Customer Created</option>
          <option value="subscription_created">Subscription Created</option>
          <option value="subscription_changed">Subscription Changed</option>
          <option value="subscription_cancelled">Subscription Cancelled</option>
          <option value="payment_succeeded">Payment Succeeded</option>
          <option value="payment_failed">Payment Failed</option>
        </select>
      </div>

      {loading && <div className="loading">Loading webhooks...</div>}

      {!loading && (
        <>
          <div className="webhook-list">
            {webhooks.map((webhook) => (
              <div key={webhook.eventId} className="webhook-item">
                <div className="webhook-header">
                  <div className="webhook-type">{webhook.eventType}</div>
                  <span className={`event-status ${webhook.status}`}>
                    {webhook.status}
                  </span>
                </div>
                <div className="webhook-meta">
                  <span>Event ID: {webhook.eventId}</span>
                  {webhook.userEmail && <span>Email: {webhook.userEmail}</span>}
                  <span>{webhook.timestamp ? new Date(webhook.timestamp).toLocaleString() : 'N/A'}</span>
                  {webhook.userId && <span>User: {webhook.userId}</span>}
                </div>
                <div className="webhook-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedWebhook(webhook)}
                  >
                    View Details
                  </button>
                  {webhook.status === PORTAL_STATUS.FAILED && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleReprocess(webhook.eventId)}
                    >
                      Reprocess
                    </button>
                  )}
                  {(webhook.status === PORTAL_STATUS.FAILED || (!webhook.linkedUserId && webhook.status !== PORTAL_STATUS.COMPLETED)) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleForceLink(webhook.eventId)}
                    >
                      Force Link
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {total > 0 && (
            <Pagination
              currentPage={page}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}
        </>
      )}

      {selectedWebhook && (
        <div className="modal-overlay" onClick={() => setSelectedWebhook(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Webhook Details</h3>
              <button className="modal-close" onClick={() => setSelectedWebhook(null)}>
                <CloseIcon />
              </button>
            </div>

            <div className="payload-section">
              <h4>Event Information</h4>
              <div className="webhook-meta" style={{ marginBottom: '1rem' }}>
                <span><strong>Type:</strong> {selectedWebhook.eventType}</span>
                <span><strong>Status:</strong> {selectedWebhook.status}</span>
                <span><strong>Time:</strong> {selectedWebhook.timestamp ? new Date(selectedWebhook.timestamp).toLocaleString() : 'N/A'}</span>
              </div>
            </div>

            <div className="payload-section">
              <h4>Headers</h4>
              <div className="payload-content">
                {selectedWebhook.rawHeaders ? JSON.stringify(selectedWebhook.rawHeaders, null, 2) : 'No headers available'}
              </div>
            </div>

            <div className="payload-section">
              <h4>Payload</h4>
              <div className="payload-content">
                {selectedWebhook.webhookPayload ? JSON.stringify(selectedWebhook.webhookPayload, null, 2) : 'No payload available'}
              </div>
            </div>

            {selectedWebhook.resultError && (
              <div className="payload-section">
                <h4>Error</h4>
                <div className="payload-content" style={{ color: '#ef4444' }}>
                  {selectedWebhook.resultError}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WebhookInbox;